import React, { useState } from 'react';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

export default function SurveyResultPage() {
    // Mock response data
    const surveyData = {
        survey: {
            id: 1,
            title: "Test Survey 2",
            description: "Test Description of the survey 2",
            category: "Employee Satisfaction",
            status: "draft"
        },
        stats: {
            totalResponses: 2847,
            completionRate: 87.3,
            averageTime: "3m 24s",
            responsesTrend: [
                { day: 'Mon', responses: 145 },
                { day: 'Tue', responses: 312 },
                { day: 'Wed', responses: 428 },
                { day: 'Thu', responses: 521 },
                { day: 'Fri', responses: 687 },
                { day: 'Sat', responses: 412 },
                { day: 'Sun', responses: 342 }
            ]
        },
        questions: [
            {
                id: 1,
                type: "text",
                title: "Text Input Question",
                totalResponses: 2485,
                responseRate: 87.3,
                sampleResponses: [
                    "Great experience with the product",
                    "Very satisfied with the service",
                    "Could improve customer support"
                ]
            },
            {
                id: 2,
                type: "text",
                title: "Text Input Question",
                totalResponses: 2521,
                responseRate: 88.6,
                sampleResponses: [
                    "Outstanding performance",
                    "Met expectations",
                    "Needs improvement"
                ]
            },
            {
                id: 3,
                type: "single_choice",
                title: "Single Choice Question",
                totalResponses: 2847,
                responseRate: 100,
                options: [
                    { text: "Option 1", value: "option_1", count: 1698, percentage: 59.7 },
                    { text: "Option 2", value: "option_2", count: 1149, percentage: 40.3 }
                ]
            },
            {
                id: 4,
                type: "multiple_choice",
                title: "Multiple Choice Question",
                totalResponses: 2847,
                responseRate: 100,
                options: [
                    { text: "Option 1", value: "option_1", count: 1923, percentage: 67.5 },
                    { text: "Option 2", value: "option_2", count: 1402, percentage: 49.3 }
                ]
            },
            {
                id: 5,
                type: "dropdown",
                title: "Dropdown Question",
                totalResponses: 2847,
                responseRate: 100,
                options: [
                    { text: "Option 1", value: "option_1", count: 1134, percentage: 39.8 },
                    { text: "Option 2", value: "option_2", count: 1713, percentage: 60.2 }
                ]
            },
            {
                id: 6,
                type: "rating",
                title: "Rating Question",
                totalResponses: 2847,
                responseRate: 100,
                averageRating: 4.2,
                distribution: [
                    { rating: "1 - Poor", count: 89, percentage: 3.1 },
                    { rating: "2 - Fair", count: 234, percentage: 8.2 },
                    { rating: "3 - Good", count: 512, percentage: 18.0 },
                    { rating: "4 - Very Good", count: 1156, percentage: 40.6 },
                    { rating: "5 - Excellent", count: 856, percentage: 30.1 }
                ]
            }
        ]
    };

    const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6'];

    const StatCard = ({ icon, title, value, percentage, color }) => (
        <div className={`col-12 col-sm-6 col-lg-3 mb-4`}>
            <div className="card h-100 shadow-sm border-0" style={{ borderLeft: `4px solid ${color}` }}>
                <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                        <i className={`bi ${icon} fs-4`} style={{ color }}></i>
                    </div>
                    <h6 className="card-title text-muted mb-2">{title}</h6>
                    <h3 className="mb-0" style={{ color }}>{value}</h3>
                    {percentage && <small className="text-success">↑ {percentage}</small>}
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '2rem 0' }}>
            <div className="container-fluid">
                {/* Header */}
                <div className="mb-4">
                    <div className="card border-0 shadow-sm" style={{ borderBottom: '3px solid #4f46e5' }}>
                        <div className="card-body p-4">
                            <h1 className="mb-2" style={{ color: '#4f46e5', fontWeight: '700' }}>
                                <i className="bi bi-graph-up me-2"></i>Survey Results Dashboard
                            </h1>
                            <p className="text-muted mb-2">{surveyData.survey.title}</p>
                            <small className="text-secondary">{surveyData.survey.description}</small>
                        </div>
                    </div>
                </div>

                {/* Key Stats */}
                <div className="row mb-4">
                    <StatCard icon="bi-check-circle" title="Total Responses" value={surveyData.stats.totalResponses.toLocaleString()} percentage="12.5%" color="#4f46e5" />
                    <StatCard icon="bi-percent" title="Completion Rate" value={`${surveyData.stats.completionRate}%`} percentage="2.3%" color="#10b981" />
                    <StatCard icon="bi-clock" title="Avg. Time" value={surveyData.stats.averageTime} color="#f59e0b" />
                    <StatCard icon="bi-chat-dots" title="Response Category" value={surveyData.survey.category} color="#06b6d4" />
                </div>

                {/* Response Trend */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom p-4">
                                <h5 className="mb-0" style={{ color: '#4f46e5' }}>
                                    <i className="bi bi-graph-up-arrow me-2"></i>Response Trend
                                </h5>
                            </div>
                            <div className="card-body p-4">
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={surveyData.stats.responsesTrend}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="day" stroke="#6b7280" />
                                        <YAxis stroke="#6b7280" />
                                        <Tooltip contentStyle={{ backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px' }} />
                                        <Line type="monotone" dataKey="responses" stroke="#4f46e5" strokeWidth={3} dot={{ fill: '#4f46e5', r: 5 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Questions Results */}
                {surveyData.questions.map((question, idx) => (
                    <div key={question.id} className="row mb-4">
                        <div className="col-12">
                            <div className="card shadow-sm border-0">
                                <div className="card-header bg-white border-bottom p-4">
                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                        <div>
                                            <h5 className="mb-2" style={{ color: '#4f46e5' }}>
                                                <i className="bi bi-question-circle me-2"></i>
                                                Q{idx + 1}. {question.title}
                                            </h5>
                                            <small className="text-muted">Type: {question.type.replace(/_/g, ' ').toUpperCase()}</small>
                                        </div>
                                        <div className="text-end">
                      <span className="badge bg-primary-light text-primary p-2" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                        {question.totalResponses.toLocaleString()} responses
                      </span>
                                            <div className="small text-success mt-1">Response rate: {question.responseRate}%</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body p-4">
                                    {(question.type === 'single_choice' || question.type === 'multiple_choice' || question.type === 'dropdown') && (
                                        <div className="row">
                                            <div className="col-12 col-md-6">
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <PieChart>
                                                        <Pie
                                                            data={question.options}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ name, percentage }) => `${name}: ${percentage}%`}
                                                            outerRadius={80}
                                                            fill="#8884d8"
                                                            dataKey="percentage"
                                                        >
                                                            {question.options.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => `${value}%`} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                {question.options.map((option, i) => (
                                                    <div key={i} className="mb-3">
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="fw-500">{option.text}</span>
                                                            <span className="text-muted">{option.count.toLocaleString()}</span>
                                                        </div>
                                                        <div className="progress" style={{ height: '24px', backgroundColor: '#e5e7eb' }}>
                                                            <div
                                                                className="progress-bar"
                                                                style={{
                                                                    width: `${option.percentage}%`,
                                                                    backgroundColor: COLORS[i % COLORS.length]
                                                                }}
                                                            >
                                                                <span className="ps-2 text-white small fw-bold">{option.percentage}%</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {question.type === 'rating' && (
                                        <div className="row">
                                            <div className="col-12 col-md-6">
                                                <div className="text-center mb-4">
                                                    <div className="display-4" style={{ color: '#10b981' }}>
                                                        {question.averageRating}
                                                        <span className="fs-5">/5</span>
                                                    </div>
                                                    <div className="text-warning">
                                                        {'★'.repeat(Math.round(question.averageRating))}
                                                        {'☆'.repeat(5 - Math.round(question.averageRating))}
                                                    </div>
                                                    <p className="text-muted mt-2">Average Rating</p>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <BarChart data={question.distribution} layout="vertical">
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                        <XAxis type="number" stroke="#6b7280" />
                                                        <YAxis dataKey="rating" type="category" stroke="#6b7280" width={100} />
                                                        <Tooltip contentStyle={{ backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px' }} />
                                                        <Bar dataKey="count" fill="#4f46e5" radius={[0, 8, 8, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}

                                    {question.type === 'text' && (
                                        <div>
                                            <h6 className="mb-3" style={{ color: '#4f46e5' }}>Sample Responses</h6>
                                            <div className="list-group">
                                                {question.sampleResponses.map((response, i) => (
                                                    <div key={i} className="list-group-item border-0 border-bottom mb-2 pb-2">
                                                        <div className="d-flex">
                                                            <i className="bi bi-chat-left-text text-success me-2 mt-1"></i>
                                                            <div>
                                                                <p className="mb-0" style={{ color: '#374151' }}>{response}</p>
                                                                <small className="text-muted">Response #{i + 1}</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Footer */}
                <div className="row mt-5">
                    <div className="col-12">
                        <div className="text-center text-muted">
                            <small>
                                <i className="bi bi-info-circle me-1"></i>
                                Last updated: {new Date().toLocaleString()}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}